/**
 * CAD Parser Service
 *
 * Turns real DWG/DXF file content into flat 2D line-work geometry plus layer
 * metadata so the scene renderers can display a CAD drawing exactly like
 * native CAD tools does (modelspace entities, block insertions expanded, XREFs
 * included, layers, ACI/truecolor colors, paper-space text as labels).
 *
 * Supported formats:
 * - DXF: ASCII DXF only (releases R9-R12 up to current, AC1006 ... AC1036)
 *   — parsed with `dxf-parser`. Binary DXF is detected and rejected with an
 *   actionable message (re-save as ASCII DXF or link the source DWG).
 * - DWG: binary DWG, R13 (AC1009) through current DWG releases — parsed with the
 *   LibreDWG WebAssembly build (`@mlightcad/libredwg-web`).
 * - SVG is rasterized elsewhere; this service only handles DWG/DXF.
 *
 * Everything is normalized to project meters on the XY plane (DXF OCS Y
 * flipped to WCS Z-up convention of the scene: CAD (x, y) -> scene (x, y, z=elev)).
 */

// The parser itself is loaded dynamically at the first DXF parse (see
// parseDxfText): keeping it a value import pinned the whole library into the
// eager chunk via CommandRegistrar → LinkCADTool. Types are erased at compile
// time, so only the type import stays static.
import type DxfParser from 'dxf-parser';
import type { IDxf } from 'dxf-parser';

// ==================== Public types ====================

export interface CadLine {
    /** Two endpoints, already unit-scaled into project coordinates. */
    points: [[number, number, number], [number, number, number]];
    /** Hex color resolved from entity color / layer color / ACI table. */
    color: string;
    /** Owning layer name (empty when unknown). */
    layer: string;
    /** Linetype dash pattern in world units: [on, off, on, off…]; absent = continuous. */
    dash?: number[];
    /** Polyline constant width (DXF group 43): draws a parallel offset edge. */
    width?: number;
}

export interface CadText {
    x: number;
    y: number;
    z: number;
    text: string;
    height: number;
    rotation: number; // degrees, CCW
    color: string;
    layer: string;
}

export interface CadLayerInfo {
    name: string;
    visible: boolean;
    color: string;
    frozen: boolean;
    locked: boolean;
}

/** Block definition carried by the file (DXF BLOCKS section / DWG block
 *  map). Feeds the native-import path (Faz 9.5) when seeding the block
 *  store; the display pipeline keeps expanding INSERTs inline as before. */
export interface CadBlockInfo {
    name: string;
    lines: CadLine[];
    texts: CadText[];
}

/** Underlay reference (PDFUNDERLAY/IMAGE): the source file path is kept so
 *  the Xref panel can flag it as missing until it is re-attached (Faz 9.8). */
export interface CadUnderlayInfo {
    name: string;
    path: string;
    layer: string;
    insertion: [number, number, number];
    width: number;
    height: number;
}

export interface CadParseResult {
    fileName: string;
    /** Source format that was actually parsed. */
    format: 'dxf' | 'dwg';
    /** Drawing bounding box in project meters: [minX, minY, minZ, maxX, maxY, maxZ]. */
    bounds: [number, number, number, number, number, number];
    /** Uniform scale factor applied (file units -> meters). */
    unitScale: number;
    /** $INSUNITS code of the source file (0 = unitless). */
    insunits: number;
    lines: CadLine[];
    texts: CadText[];
    layers: CadLayerInfo[];
    /** Present only when the file carries non-empty block definitions. */
    blocks?: CadBlockInfo[];
    /** Present only when the file references raster/PDF underlays. */
    underlays?: CadUnderlayInfo[];
}

// ==================== Constants ====================

/** Max segments used to tessellate a full circle/ellipse; arcs scale with sweep. */
const CURVE_SEGMENTS = 64;
/** Hard cap so pathological files can't lock the UI thread. */
const MAX_ENTITIES_PER_PARSE = 200000;

/** ACI (color index (DXF)) -> hex. Full 255-entry table generated at module load. */
const ACI_TABLE: string[] = buildAciTable();

// ==================== ACI color table ====================

/**
 * Builds the canonical 255-entry ACI color index table. ACI 1-9 are the
 * standard bright colors, 10-249 are a 249-step hue/value ramp (25 hue groups
 * x ~10 lightness steps), 250-255 are the greys.
 */
function buildAciTable(): string[] {
    const base: Array<[number, number, number]> = [
        [0, 0, 0],          // 0 unused placeholder
        [255, 0, 0],        // 1 red
        [255, 255, 0],      // 2 yellow
        [0, 255, 0],        // 3 green
        [0, 255, 255],      // 4 cyan
        [0, 0, 255],        // 5 blue
        [255, 0, 255],      // 6 magenta
        [255, 255, 255],    // 7 white
        [128, 128, 128],    // 8 grey
        [192, 192, 192],    // 9 light grey
    ];
    const table: string[] = base.map(rgb => rgbToHex(rgb[0], rgb[1], rgb[2]));
    // ACI 10..249: 5-row blocks of 25 hues; each block darkens the previous.
    // Standard mapping: index = 10 + 25*row + col, row 0..4 (full..darkest),
    // col picks hue and saturation from the hue ring.
    for (let i = 10; i < 250; i++) {
        const row = Math.floor((i - 10) / 25);       // 0..9 -> lightness band
        const col = (i - 10) % 25;                    // hue/saturation slot
        const hue = (col / 25) * 360;
        // 10 lightness bands: row maps band 0..9 -> saturation/lightness mix.
        const sat = col % 2 === 0 ? 100 : 65;         // alternating saturation
        const light = 90 - row * 8;                   // descending brightness
        table.push(hslToHex(hue, sat, light));
    }
    // ACI 250..255: greys from dark to light
    const greys = [51, 91, 132, 173, 214, 255];
    for (const g of greys) table.push(rgbToHex(g, g, g));
    return table;
}

function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

function hslToHex(h: number, s: number, l: number): string {
    s /= 100; l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return rgbToHex(Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255));
}

/** Resolve an ACI index (1..255) to hex; fallback to default. */
export function aciToHex(index: number, fallback = '#c8c8c8'): string {
    if (!Number.isFinite(index) || index <= 0 || index > 255) return fallback;
    return ACI_TABLE[Math.round(index)] ?? fallback;
}

/** Resolve a 24-bit truecolor integer (DXF 420 / DWG color.rgb) to hex. */
function trueColorHex(value: number): string | null {
    if (!Number.isFinite(value) || value < 0) return null;
    const r = (value >> 16) & 0xff;
    const g = (value >> 8) & 0xff;
    const b = value & 0xff;
    return rgbToHex(r, g, b);
}

// ==================== Unit scaling ====================

/**
 * DXF/DWG $INSUNITS header code -> meters (official DXF list).
 * Unknown/0 keeps unitScale 1 so drawings with no declared units import
 * at 1:1 (the "Specify unit = None" behaviour).
 */
function insunitsToMeters(code: number | undefined): number {
    switch (code) {
        case 1: return 0.0254;        // Inches
        case 2: return 0.3048;        // Feet
        case 3: return 1609.344;      // Miles
        case 4: return 0.001;         // Millimeters
        case 5: return 0.01;          // Centimeters
        case 6: return 1;             // Meters
        case 7: return 1000;          // Kilometers
        case 8: return 0.0000000254;  // Microinches
        case 9: return 0.0000254;     // Mils (thousandth of an inch)
        case 10: return 0.0042333;    // Picas
        case 11: return 1e-10;        // Angstroms
        case 12: return 1e-9;         // Nanometers
        case 13: return 1e-6;         // Microns
        case 14: return 0.1;          // Decimeters
        case 15: return 10;           // Decameters
        case 16: return 100;          // Hectometers
        case 17: return 1e9;          // Gigameters
        default: return 1;            // unitless or exotic -> 1:1 into project meters
    }
}

/** DWG header may carry insertion units under different key spellings. */
function readDwgInsunits(header: Record<string, unknown> | undefined): number | undefined {
    if (!header) return undefined;
    const v = (header as any).INSUNITS ?? (header as any).insunits;
    return typeof v === 'number' ? v : undefined;
}

// ==================== Geometry helpers ====================

type Pt3 = [number, number, number];

function clampEntities<T>(arr: T[], budget: { left: number }): T[] {
    if (budget.left <= 0) return [];
    const take = arr.length > budget.left ? arr.slice(0, budget.left) : arr;
    budget.left -= take.length;
    return take;
}

function pushSeg(out: CadLine[], a: Pt3, b: Pt3, color: string, layer: string, dash?: number[], width?: number) {
    if (a[0] === b[0] && a[1] === b[1] && a[2] === b[2]) return;
    out.push({ points: [a, b], color, layer, ...(dash ? { dash } : {}), ...(width ? { width } : {}) });
}

// Linetype definitions (DXF LTYPE names we resolve offline; patterns are
// in drawing units: positive = draw, negative = gap).
const LINETYPE_PATTERNS: Record<string, number[]> = {
    DASHED: [1.25, -0.625],
    CENTER: [2.5, -0.625, 0.25, -0.625],
    HIDDEN: [0.375, -0.25],
    DOTTED: [0.0001, -0.25],
    PHANTOM: [2.5, -0.5, 0.5, -0.5, 0.5, -0.5],
    DASHDOT: [1.25, -0.625, 0.25, -0.625],
    DIVIDE: [0.5, -0.25, 0.25, -0.25, 0.25, -0.25],
};
/** Resolve an entity's effective dash pattern from its linetype name+scale. */
function linetypeDash(lineType: unknown, scale: unknown): number[] | undefined {
    if (typeof lineType !== 'string' || !lineType) return undefined;
    const name = lineType.trim().toUpperCase();
    if (name === 'BYLAYER' || name === 'CONTINUOUS' || name === 'BYBLOCK') return undefined;
    const pat = LINETYPE_PATTERNS[name];
    if (!pat) return undefined;
    const sc = typeof scale === 'number' && scale > 0 ? scale : 1;
    return pat.map((v) => v * sc);
}
/** DXF OCS->WCS for negative extrusion: mirror Y and reverse angles. */
function ocsFlip(extrusionZ: unknown): boolean {
    return typeof extrusionZ === 'number' && extrusionZ < 0;
}

/** Tessellate a parametric arc (center, radius, start/end angle in rad). */
function arcSegments(
    cx: number, cy: number, r: number,
    a0: number, a1: number, z: number,
    out: CadLine[], color: string, layer: string, scale: number
) {
    if (!Number.isFinite(r) || r <= 0) return;
    let sweep = a1 - a0;
    while (sweep < 0) sweep += Math.PI * 2;
    if (Math.abs(sweep) < 1e-9) sweep = Math.PI * 2;
    const segs = Math.max(6, Math.min(CURVE_SEGMENTS, Math.ceil(sweep / (Math.PI / 32))));
    let px = cx + r * Math.cos(a0), py = cy + r * Math.sin(a0), pz = z;
    for (let i = 1; i <= segs; i++) {
        const a = a0 + (sweep * i) / segs;
        const nx = cx + r * Math.cos(a), ny = cy + r * Math.sin(a);
        pushSeg(out, [px * scale, py * scale, pz * scale], [nx * scale, ny * scale, pz * scale], color, layer);
        px = nx; py = ny;
    }
}

/** Tessellate a full/partial ellipse from center + major-axis end point. */
function ellipseSegments(
    center: Pt3, majorEnd: Pt3, axisRatio: number,
    t0: number, t1: number,
    out: CadLine[], color: string, layer: string, scale: number
) {
    const mx = majorEnd[0], my = majorEnd[1];
    const rx = Math.hypot(mx, my);
    if (rx <= 0) return;
    const ry = rx * (axisRatio || 1);
    const rot = Math.atan2(my, mx);
    let span = t1 - t0;
    while (span < 0) span += Math.PI * 2;
    if (span < 1e-9) span = Math.PI * 2;
    const segs = Math.max(8, Math.min(CURVE_SEGMENTS, Math.ceil(span / (Math.PI / 32))));
    const z = center[2];
    const pt = (t: number): Pt3 => {
        const ex = rx * Math.cos(t), ey = ry * Math.sin(t);
        return [
            center[0] + ex * Math.cos(rot) - ey * Math.sin(rot),
            center[1] + ex * Math.sin(rot) + ey * Math.cos(rot),
            z,
        ];
    };
    let prev = pt(t0);
    for (let i = 1; i <= segs; i++) {
        const p = pt(t0 + (span * i) / segs);
        pushSeg(out, [prev[0] * scale, prev[1] * scale, prev[2] * scale], [p[0] * scale, p[1] * scale, p[2] * scale], color, layer);
        prev = p;
    }
}

/**
 * Polyline vertex chain with bulge (arc) segments.
 * Bulge b on edge V0->V1 means the edge is an arc whose included angle is
 * 4*atan(b) (positive CCW).
 */
function bulgeSegments(
    v0: Pt3, v1: Pt3, bulge: number,
    out: CadLine[], color: string, layer: string, scale: number
) {
    if (!bulge || Math.abs(bulge) < 1e-10) {
        pushSeg(out, [v0[0], v0[1], v0[2]], [v1[0], v1[1], v1[2]], color, layer);
        return;
    }
    const dx = v1[0] - v0[0], dy = v1[1] - v0[1];
    const d = Math.hypot(dx, dy);
    if (d < 1e-12) { pushSeg(out, [v0[0], v0[1], v0[2]], [v1[0], v1[1], v1[2]], color, layer); return; }
    const theta = 4 * Math.atan(bulge); // included angle, signed
    const r = d / (2 * Math.sin(Math.abs(theta) / 2));
    // Center: perpendicular from chord midpoint, distance = d/(2 tan(theta/2))
    const mid = [(v0[0] + v1[0]) / 2, (v0[1] + v1[1]) / 2];
    const h = d / (2 * Math.tan(theta / 2));
    // Perpendicular unit to chord, sign so the arc bulges toward +bulge side
    const px = -dy / d, py = dx / d;
    const sign = theta > 0 ? 1 : -1;
    const c = [mid[0] + px * h * sign, mid[1] + py * h * sign];
    const a0 = Math.atan2(v0[1] - c[1], v0[0] - c[0]);
    arcSegments(c[0], c[1], Math.abs(r), a0, a0 + theta, v0[2], out, color, layer, scale);
}

// ==================== DXF parsing ====================

interface DxfColorSource {
    colorIndex?: number;
    color?: number;      // truecolor int (420) when present
}

/**
 * Resolve display color for one entity, following DXF precedence:
 * entity ACI (256/0 = BYLAYER/BYBLOCK) > entity truecolor (420) >
 * layer color > default. Negative colorIndex means the entity is hidden.
 */
function resolveColor(
    entity: DxfColorSource,
    layerColor: string | undefined,
    fallback: string
): string {
    const ci = entity.colorIndex;
    if (typeof ci === 'number' && ci > 0 && ci !== 256) {
        return aciToHex(ci, fallback);
    }
    if (typeof entity.color === 'number' && entity.color > 0 && entity.color <= 0xffffff) {
        return trueColorHex(entity.color) ?? fallback;
    }
    return layerColor ?? fallback;
}

// Loose structural typing of the parsed DXF graph (dxf-parser ships partial
// typings for some entity shapes; we tolerate missing fields defensively).
interface RawDxfEntity extends DxfColorSource {
    type: string;
    layer?: string;
    visible?: boolean;
    startPoint?: { x: number; y: number; z?: number };
    endPoint?: { x: number; y: number; z?: number };
    vertices?: Array<{ x?: number; y?: number; z?: number; bulge?: number }>;
    shape?: boolean; // polyline closed flag in dxf-parser
    center?: { x: number; y: number; z?: number };
    radius?: number;
    startAngle?: number; // dxf-parser already radians for ARC
    endAngle?: number;
    majorAxisEndPoint?: { x: number; y: number; z?: number };
    axisRatio?: number;
    controlPoints?: Array<{ x: number; y: number; z?: number }>;
    fitPoints?: Array<{ x: number; y: number; z?: number }>;
    knotValues?: number[];
    degreeOfSplineCurve?: number;
    closed?: boolean;
    name?: string;        // INSERT block name
    position?: { x: number; y: number; z?: number };
    rotation?: number;    // degrees
    xScale?: number;
    yScale?: number;
    text?: string;
    textHeight?: number;
    height?: number;      // MTEXT uses height
    elevation?: number;
    depth?: number;
    actualMeasurement?: number;
    dimensionType?: number;
    block?: string;       // DIMENSION anonymous block name
    anchorPoint?: { x: number; y: number; z?: number };
    insertionPoint?: { x: number; y: number; z?: number };
}

function toPt3(p: { x: number; y: number; z?: number } | undefined, elev = 0): Pt3 {
    return [p?.x ?? 0, p?.y ?? 0, p?.z ?? elev];
}

function parseDxfEntities(
    entities: RawDxfEntity[],
    lines: CadLine[],
    texts: CadText[],
    budget: { left: number },
    layerColors: Map<string, string>,
    blocks: Map<string, RawDxfEntity[]>,
    scale: number,
    blockVisited?: Set<string>
) {
    for (const e of entities) {
        if (budget.left <= 0) return;
        if (e.visible === false) continue; // skip explicitly invisible
        const layer = e.layer ?? '0';
        const color = resolveColor(e, layerColors.get(layer), '#c8c8c8');
        const z = (e as any).elevation ?? 0;
        const t: RawDxfEntity = e;
        switch (t.type) {
            case 'LINE': {
                // dxf-parser stores both endpoints in `vertices` (start first)
                const flip = ocsFlip((t as any).extrusionDirectionZ);
                const dash = linetypeDash((t as any).lineType, (t as any).lineTypeScale);
                const fy = (v: number | undefined) => (flip ? -(v ?? 0) : (v ?? 0));
                const lv = t.vertices;
                if (Array.isArray(lv) && lv.length >= 2) {
                    const a = lv[0], b = lv[1];
                    pushSeg(lines,
                        [(a.x ?? 0) * scale, fy(a.y) * scale, (a.z ?? z) * scale],
                        [(b.x ?? 0) * scale, fy(b.y) * scale, (b.z ?? z) * scale],
                        color, layer, dash);
                } else if (t.startPoint && t.endPoint) {
                    pushSeg(lines,
                        [t.startPoint.x * scale, fy(t.startPoint.y) * scale, (t.startPoint.z ?? z) * scale],
                        [t.endPoint.x * scale, fy(t.endPoint.y) * scale, (t.endPoint.z ?? z) * scale],
                        color, layer, dash);
                }
                break;
            }
            case 'LWPOLYLINE':
            case 'POLYLINE': {
                const vs = t.vertices ?? [];
                if (vs.length < 2) break;
                const flip = ocsFlip((t as any).extrusionDirectionZ);
                const pts = vs.map(v => ({
                    x: (v.x ?? 0) * scale,
                    y: (flip ? -(v.y ?? 0) : (v.y ?? 0)) * scale,
                    z: (v.z ?? z) * scale,
                    bulge: flip ? -(v.bulge ?? 0) : (v.bulge ?? 0),
                }));
                const n = pts.length;
                const closed = !!(t as any).shape || !!(t as any).closed;
                const last = closed ? n : n - 1;
                // DXF constant polyline width (43) + entity/layer linetype.
                const pw = typeof (t as any).width === 'number' ? (t as any).width * scale : undefined;
                const dash = linetypeDash((t as any).lineType, (t as any).lineTypeScale);
                const start = lines.length;
                for (let i = 0; i < last; i++) {
                    const a = pts[i], b = pts[(i + 1) % n];
                    bulgeSegments([a.x, a.y, a.z], [b.x, b.y, b.z], a.bulge, lines, color, layer, 1);
                }
                if (pw || dash) {
                    for (let i = start; i < lines.length; i++) {
                        if (pw) lines[i].width = pw;
                        if (dash) lines[i].dash = dash;
                    }
                }
                break;
            }
            case 'ARC': {
                if (!t.center || typeof t.radius !== 'number') break;
                const flip = ocsFlip((t as any).extrusionDirectionZ);
                const start = lines.length;
                arcSegments(t.center.x * scale, (flip ? -t.center.y : t.center.y) * scale, t.radius * scale,
                    flip ? -(t.endAngle ?? Math.PI * 2) : (t.startAngle ?? 0),
                    flip ? -(t.startAngle ?? 0) : (t.endAngle ?? Math.PI * 2),
                    (t.center.z ?? z) * scale, lines, color, layer, 1);
                const dash = linetypeDash((t as any).lineType, (t as any).lineTypeScale);
                if (dash) for (let i = start; i < lines.length; i++) lines[i].dash = dash;
                break;
            }
            case 'CIRCLE': {
                if (!t.center || typeof t.radius !== 'number') break;
                const flip = ocsFlip((t as any).extrusionDirectionZ);
                const start = lines.length;
                arcSegments(t.center.x, (flip ? -t.center.y : t.center.y), t.radius, 0, Math.PI * 2, t.center.z ?? z, lines, color, layer, scale);
                const dash = linetypeDash((t as any).lineType, (t as any).lineTypeScale);
                if (dash) for (let i = start; i < lines.length; i++) lines[i].dash = dash;
                break;
            }
            case 'ELLIPSE': {
                if (!t.center || !t.majorAxisEndPoint) break;
                const startP = t as any;
                const start = lines.length;
                ellipseSegments(
                    toPt3(t.center, z),
                    toPt3({ x: startP.majorAxisEndPoint.x, y: startP.majorAxisEndPoint.y, z: startP.majorAxisEndPoint.z ?? 0 }),
                    t.axisRatio ?? 1,
                    startP.startAngle ?? 0, startP.endAngle ?? Math.PI * 2,
                    lines, color, layer, scale);
                const dash = linetypeDash(startP.lineType, startP.lineTypeScale);
                if (dash) for (let i = start; i < lines.length; i++) lines[i].dash = dash;
                break;
            }
            case 'HATCH': {
                // dxf-parser gives paths with bulge-aware vertices (edge
                // polygons) or raw boundary paths; draw every polygon path
                // closed so the filled region's outline appears, plus a
                // sparse dot grid for the fill impression when pattern=0.
                const paths = Array.isArray((t as any).paths) ? (t as any).paths : [];
                for (const path of paths) {
                    const loops = Array.isArray(path.boundaryLoops) ? path.boundaryLoops : [];
                    for (const loop of loops) {
                        const edges = Array.isArray(loop.edges) ? loop.edges : [];
                        const pts: Pt3[] = [];
                        for (const edge of edges) {
                            const vs = Array.isArray(edge.polyline?.vertices) ? edge.polyline.vertices : null;
                            if (vs) {
                                const n = vs.length;
                                for (let i = 0; i < n; i++) {
                                    const v = vs[i];
                                    pts.push([(v.x ?? 0) * scale, (v.y ?? 0) * scale, (v.z ?? z) * scale]);
                                }
                            } else if (edge.start && edge.end) {
                                pts.push([edge.start.x * scale, edge.start.y * scale, z * scale]);
                                pts.push([edge.end.x * scale, edge.end.y * scale, z * scale]);
                            }
                        }
                        for (let i = 1; i < pts.length; i++) {
                            pushSeg(lines, pts[i - 1], pts[i], color, layer);
                        }
                        if (pts.length >= 3) pushSeg(lines, pts[pts.length - 1], pts[0], color, layer);
                    }
                }
                break;
            }
            case 'IMAGE':
            case 'PDFUNDERLAY':
            case 'UNDERLAY': {
                // Raster/PDF underlay: outline quad from insertion + size
                // (image/geometry extents in the DXF frame) so at least the
                // placement is visible as a frame.
                const pos = (t as any).insertionPoint ?? (t as any).position;
                const sz = (t as any).sizeInWorldUnits === 1 ? (t as any).size : null;
                const w = (sz && sz.x) || ((t as any).width ?? 1) * scale;
                const h = (sz && sz.y) || ((t as any).height ?? 1) * scale;
                if (pos) {
                    const x = pos.x * scale, y = pos.y * scale, zz = (pos.z ?? z) * scale;
                    pushSeg(lines, [x, y, zz], [x + w, y, zz], color, layer);
                    pushSeg(lines, [x + w, y, zz], [x + w, y + h, zz], color, layer);
                    pushSeg(lines, [x + w, y + h, zz], [x, y + h, zz], color, layer);
                    pushSeg(lines, [x, y + h, zz], [x, y, zz], color, layer);
                }
                break;
            }
            case 'SPLINE': {
                const ctrl = t.controlPoints ?? t.fitPoints ?? [];
                if (ctrl.length < 2) break;
                // Render control-point polyline (and prefer fit points if present)
                const chain = (t.fitPoints && t.fitPoints.length > 1 ? t.fitPoints : ctrl);
                for (let i = 1; i < chain.length; i++) {
                    pushSeg(lines,
                        [chain[i - 1].x * scale, chain[i - 1].y * scale, (chain[i - 1].z ?? z) * scale],
                        [chain[i].x * scale, chain[i].y * scale, (chain[i].z ?? z) * scale],
                        color, layer);
                }
                if (t.closed) {
                    pushSeg(lines,
                        [chain[chain.length - 1].x * scale, chain[chain.length - 1].y * scale, z * scale],
                        [chain[0].x * scale, chain[0].y * scale, z * scale],
                        color, layer);
                }
                break;
            }
            case 'POINT': {
                const p = toPt3((e as any).position ?? (e as any).point ?? { x: 0, y: 0 }, z);
                const s = 0.05;
                pushSeg(lines, [p[0] - s, p[1], p[2]], [p[0] + s, p[1], p[2]], color, layer);
                pushSeg(lines, [p[0], p[1] - s, p[2]], [p[0], p[1] + s, p[2]], color, layer);
                break;
            }
            case '3DFACE':
            case 'SOLID': {
                const pts = (e as any).points;
                const vs = Array.isArray(t.vertices) && t.vertices.length >= 3
                    ? t.vertices
                    : (Array.isArray(pts) ? pts : []);
                if (vs.length >= 3) {
                    const a = toPt3(vs[0], z), b = toPt3(vs[1], z), c = toPt3(vs[2], z);
                    const d = vs[3] ? toPt3(vs[3], z) : null;
                    pushSeg(lines, a, b, color, layer);
                    pushSeg(lines, b, c, color, layer);
                    if (d) { pushSeg(lines, c, d, color, layer); pushSeg(lines, d, a, color, layer); }
                    else pushSeg(lines, c, a, color, layer);
                }
                break;
            }
            case 'INSERT': {
                const blockName = t.name ?? '';
                if (!blockName) break;
                const visited = blockVisited ?? new Set<string>();
                if (visited.has(blockName)) break; // guard against recursive XREFs
                const block = blocks.get(blockName) ?? blocks.get(blockName.toUpperCase());
                if (!block || block.length === 0) break;
                visited.add(blockName);
                const ix = t.position?.x ?? 0, iy = t.position?.y ?? 0, iz = t.position?.z ?? 0;
                const rot = THREE_DEG2RAD(t.rotation ?? 0);
                const sx = t.xScale ?? 1, sy = t.yScale ?? 1;
                // Transform block entities into model space, then flatten them.
                // Recurse at scale 1: unit scaling happens once, in
                // transformInsert, so nested INSERTs never double-apply it.
                const tmpLines: CadLine[] = [];
                const tmpTexts: CadText[] = [];
                parseDxfEntities(block, tmpLines, tmpTexts, budget, layerColors, blocks, 1, visited);
                for (const ln of tmpLines) {
                    const seg: Pt3[] = ln.points.map(([x, y, zz]) => transformInsert(x, y, zz, ix, iy, iz, rot, sx, sy, scale));
                    pushSeg(lines, seg[0], seg[1], ln.color, ln.layer);
                }
                for (const tx of tmpTexts) {
                    const [x, y, zz] = transformInsert(tx.x, tx.y, tx.z, ix, iy, iz, rot, sx, sy, scale);
                    texts.push({ ...tx, x, y, z: zz, height: tx.height * Math.abs(sy) * scale, rotation: tx.rotation + (t.rotation ?? 0) });
                }
                visited.delete(blockName);
                break;
            }
            case 'TEXT':
            case 'MTEXT':
            case 'ATTDEF':
            case 'ATTRIB': {
                const s = String(t.text ?? '').trim();
                if (!s) break;
                const pos = t.startPoint ?? t.position ?? { x: 0, y: 0 };
                const height = (t.textHeight ?? t.height ?? 2.5);
                texts.push({
                    x: pos.x * scale, y: pos.y * scale, z: (pos.z ?? z) * scale,
                    text: cleanMText(s),
                    height: height * scale,
                    rotation: t.rotation ?? 0,
                    color, layer,
                });
                break;
            }
            case 'DIMENSION': {
                // Rendered via its anonymous block if present; name starts with '*D'.
                // Dimension block content is stored in ABSOLUTE coordinates with a
                // (0,0,0) block base — both by AutoCAD and by the native DXF writer —
                // so the synthetic INSERT must land at the origin, NOT at the
                // anchor/text point (which would double-offset the geometry).
                if (t.block) {
                    parseDxfEntities([{
                        type: 'INSERT',
                        name: t.block,
                        position: { x: 0, y: 0, z: 0 },
                        layer, colorIndex: e.colorIndex, color: e.color,
                    } as RawDxfEntity], lines, texts, budget, layerColors, blocks, scale, blockVisited);
                }
                break;
            }
            default:
                break;
        }
        budget.left--;
    }
}

function THREE_DEG2RAD(d: number): number { return d * Math.PI / 180; }

function transformInsert(
    x: number, y: number, z: number,
    ix: number, iy: number, iz: number,
    rot: number, sx: number, sy: number, scale: number
): Pt3 {
    const px = x * sx, py = y * sy;
    const c = Math.cos(rot), s = Math.sin(rot);
    return [
        (ix + px * c - py * s) * scale,
        (iy + px * s + py * c) * scale,
        (iz + z) * scale,
    ];
}

/** Strip MTEXT formatting codes (\P newline, \f..., {...} groups, etc.). */
function cleanMText(raw: string): string {
    return raw
        .replace(/\\P/gi, '\n')
        .replace(/\\[A-Za-z][^;]*;/g, '')  // \fArial|...;, \H1.5x; etc.
        .replace(/[{}]/g, '')
        .replace(/\\\\/g, '\\')
        .replace(/\\[LlOoKk]/g, '');
}

// ==================== DWG (LibreDWG wasm) parsing ====================

let libredwgPromise: Promise<any> | null = null;

/** Lazy singleton: loads the LibreDWG wasm module once per session. */
async function getLibreDwg(): Promise<any> {
    if (!libredwgPromise) {
        libredwgPromise = (async () => {
            const mod = await import('@mlightcad/libredwg-web');
            return await mod.LibreDwg.create(`${import.meta.env.BASE_URL}cad-wasm`);
        })().catch(err => {
            libredwgPromise = null;
            throw err;
        });
    }
    return libredwgPromise;
}

/**
 * Convert LibreDWG DwgDatabase entities to our flat geometry. The DWG object
 * graph mirrors DXF closely (same ACI colors, $INSUNITS header, block records
 * with INSERT refs), so we reuse the DXF walker via light adapters.
 */
function dwgToRawEntities(db: any): RawDxfEntity[] {
    const out: RawDxfEntity[] = [];
    const ents = Array.isArray(db?.entities) ? db.entities : [];
    for (const e of ents) {
        if (!e || typeof e !== 'object') continue;
        const raw: RawDxfEntity = {
            type: String(e.type ?? '').toUpperCase(),
            layer: e.layer ?? '0',
            colorIndex: typeof e.colorIndex === 'number' ? e.colorIndex : undefined,
            color: typeof e.color === 'number' ? e.color : undefined,
            // libredwg reports visibility as boolean true/false (not the
            // DXF 1=invisible flag): only an explicit false/0 hides.
            visible: !(e.isVisible === false || e.isVisible === 0),
        };
        // Map the handful of geometry-bearing fields entity-by-entity.
        switch (raw.type) {
            case 'LINE':
                raw.startPoint = pt3(e.startPoint); raw.endPoint = pt3(e.endPoint); break;
            case 'ARC':
                raw.center = pt3(e.center); raw.radius = e.radius;
                raw.startAngle = e.startAngle ?? 0; raw.endAngle = e.endAngle ?? Math.PI * 2; break;
            case 'CIRCLE':
                raw.center = pt3(e.center); raw.radius = e.radius;
                raw.startAngle = 0; raw.endAngle = Math.PI * 2; break;
            case 'LWPOLYLINE': {
                const vs = Array.isArray(e.vertices) ? e.vertices : [];
                raw.vertices = vs.map((v: any) => ({ x: v.x, y: v.y, z: 0, bulge: v.bulge ?? 0 }));
                if (typeof e.width === 'number') (raw as any).width = e.width;
                raw.shape = !!(e.flag & 1);
                break;
            }
            case 'POLYLINE2D': {
                const vs = Array.isArray(e.vertices) ? e.vertices : [];
                raw.vertices = vs.map((v: any) => ({ x: v.x, y: v.y, z: v.z ?? 0, bulge: v.bulge ?? 0 }));
                raw.shape = !!(e.flag & 1);
                break;
            }
            case 'POLYLINE3D': {
                const vs = Array.isArray(e.vertices) ? e.vertices : [];
                raw.vertices = vs.map((v: any) => ({ x: v.x, y: v.y, z: v.z ?? 0, bulge: 0 }));
                raw.shape = !!(e.flag & 2);
                break;
            }
            case 'ELLIPSE':
                raw.center = pt3(e.center); raw.majorAxisEndPoint = pt3(e.majorAxisEndPoint);
                raw.axisRatio = e.axisRatio; raw.startAngle = e.startAngle; raw.endAngle = e.endAngle; break;
            case 'SPLINE':
                raw.controlPoints = (Array.isArray(e.controlPoints) ? e.controlPoints : []).map((p: any) => ({ x: p.x, y: p.y, z: p.z ?? 0 }));
                raw.fitPoints = (Array.isArray(e.fitPoints) ? e.fitPoints : []).map((p: any) => ({ x: p.x, y: p.y, z: p.z ?? 0 }));
                raw.closed = !!(e.flag & 1); break;
            case 'INSERT':
                raw.name = e.name; raw.position = pt3(e.insertionPoint);
                raw.xScale = e.xScale ?? 1; raw.yScale = e.yScale ?? 1;
                // DWG stores radians internally; the shared walker expects degrees
                // exactly like dxf-parser emits them.
                raw.rotation = rad2deg(e.rotation);
                raw.block = e.name; break;
            case 'TEXT':
                raw.startPoint = { x: e.startPoint?.x ?? 0, y: e.startPoint?.y ?? 0 };
                raw.text = e.text; raw.textHeight = e.textHeight; raw.rotation = rad2deg(e.rotation); break;
            case 'MTEXT':
                raw.position = pt3(e.insertionPoint); raw.text = e.text;
                raw.height = e.textHeight; raw.rotation = rad2deg(e.rotation); break;
            case '3DFACE': {
                const c1 = pt3(e.corner1), c2 = pt3(e.corner2), c3 = pt3(e.corner3), c4 = pt3(e.corner4);
                raw.vertices = [c1, c2, c3, c4].filter(Boolean) as any; break;
            }
            case 'SOLID': {
                const c1 = pt2(e.corner1), c2 = pt2(e.corner2), c3 = pt2(e.corner3), c4 = pt2(e.corner4);
                raw.vertices = [c1, c2, c3, c4].filter(Boolean) as any; break;
            }
            case 'POINT': {
                const p = pt3(e.position); if (p) raw.position = p; break;
            }
            case 'DIMENSION': {
                raw.block = e.name; raw.anchorPoint = pt3(e.anchorPoint); raw.insertionPoint = pt3(e.insertionPoint); break;
            }
            default: break;
        }
        out.push(raw);
    }
    return out;
}

function pt3(p: any): { x: number; y: number; z?: number } | undefined {
    if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return undefined;
    return { x: p.x, y: p.y, z: p.z ?? 0 };
}
function pt2(p: any): { x: number; y: number; z?: number } | undefined {
    return pt3(p);
}
function deg2rad(d: number | undefined): number { return (d ?? 0) * Math.PI / 180; }
function rad2deg(r: number | undefined): number { return (r ?? 0) * 180 / Math.PI; }

/** DWG block record table -> name -> entity list map (mirrors DXF blocks). */
function dwgBlockMap(db: any): Map<string, RawDxfEntity[]> {
    const map = new Map<string, RawDxfEntity[]>();
    const records = db?.tables?.BLOCK_RECORD?.entries ?? [];
    for (const rec of records) {
        const name = rec?.name ?? '';
        const ents = Array.isArray(rec?.entities) ? dwgToRawEntities({ entities: rec.entities }) : [];
        if (name) map.set(name, ents);
    }
    return map;
}

function dwgLayerColors(db: any): Map<string, string> {
    const map = new Map<string, string>();
    for (const l of db?.tables?.LAYER?.entries ?? []) {
        const ci = typeof l.colorIndex === 'number' ? l.colorIndex : undefined;
        map.set(l.name, aciToHex(ci ?? 7));
    }
    return map;
}

// ==================== Binary DXF sniff ====================

/**
 * Detect Binary DXF ("Autodesk AutoCAD Binary DXF") and return the ASCII
 * stream equivalent is out of scope; dxf-parser handles ASCII only. We
 * convert binary DXF via LibreDWG wasm as well (it reads DXF binary too).
 */
function looksBinaryDxf(bytes: Uint8Array): boolean {
    // Signature: "AutoCAD Binary DXF\r\n\x1A\0"
    const sig = [65, 117, 116, 111, 67, 65, 68, 32, 66, 105, 110, 97, 114, 121, 32, 68, 88, 70, 13, 10, 26, 0];
    if (bytes.length < sig.length) return false;
    for (let i = 0; i < sig.length; i++) if (bytes[i] !== sig[i]) return false;
    return true;
}

/** ASCII DXF can be UTF-8/UTF-16/latin; decode with BOM awareness. */
function decodeDxfText(bytes: Uint8Array): string {
    if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
        return new TextDecoder('utf-16le').decode(bytes.subarray(2));
    }
    if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
        return new TextDecoder('utf-8').decode(bytes.subarray(3));
    }
    return new TextDecoder('latin1').decode(bytes);
}

// ==================== Public entry ====================

/**
 * Parse raw DWG/DXF file bytes into displayable CAD geometry.
 * Throws a descriptive Error when the format is unsupported or unreadable.
 */
export async function parseCadFile(fileName: string, bytes: ArrayBuffer): Promise<CadParseResult> {
    const lower = fileName.toLowerCase();
    const isDxf = lower.endsWith('.dxf');
    // .dwt/.dws are DWG containers with a template/standard extension
    // (OCS: "look at the bytes, not the extension" — same reader).
    const isDwgFamily = lower.endsWith('.dwg') || lower.endsWith('.dwt') || lower.endsWith('.dws');
    if (!isDxf && !isDwgFamily) {
        throw new Error(`Unsupported CAD format: ${fileName}. Only .dwg, .dwt, .dws and .dxf can be linked.`);
    }

    if (isDxf) return parseDxf(fileName, bytes);
    return parseDwg(fileName, bytes);
}

async function parseDxf(fileName: string, bytes: ArrayBuffer): Promise<CadParseResult> {
    const view = new Uint8Array(bytes);
    // Binary DXF (pre-2013 era default, rare today): not decodable without a
    // full binary tag reader; give a precise actionable error instead of a
    // broken render.
    if (looksBinaryDxf(view)) {
        throw new Error(
            `"${fileName}" is a Binary DXF. Re-save it from 2D as ASCII DXF, or link the original DWG instead.`
        );
    }
    const text = decodeDxfText(view);
    if (!/SECTION/.test(text.slice(0, 4096)) && !/AC10|AutoCAD/.test(text.slice(0, 8192))) {
        throw new Error(`"${fileName}" does not look like a DXF drawing.`);
    }
    return finish(await parseDxfText(fileName, text), fileName, 'dxf');
}

async function parseDxfText(fileName: string, text: string): Promise<{ insunits?: number; entities: RawDxfEntity[]; blocks: Map<string, RawDxfEntity[]>; layerColors: Map<string, string>; layers: CadLayerInfo[] }> {
    const { default: DxfParserClass } = await import('dxf-parser');
    const parser = new DxfParserClass();
    let dxf: IDxf | null = null;
    try {
        dxf = parser.parseSync(text);
    } catch (err) {
        throw new Error(`DXF parse failed for ${fileName}: ${(err as Error).message}`);
    }
    if (!dxf) throw new Error(`DXF parse returned nothing for ${fileName}`);

    const insunits = typeof (dxf.header as any)?.$INSUNITS === 'number' ? (dxf.header as any).$INSUNITS : undefined;

    const layerColors = new Map<string, string>();
    const layers: CadLayerInfo[] = [];
    const dxfLayers = (dxf.tables?.layer?.layers ?? {}) as Record<string, { name: string; visible?: boolean; frozen?: boolean; colorIndex?: number; color?: number; locked?: boolean }>;
    for (const key of Object.keys(dxfLayers)) {
        const l = dxfLayers[key];
        const hex = typeof l.color === 'number' && l.color >= 0 ? (trueColorHex(l.color) ?? aciToHex(l.colorIndex ?? 7)) : aciToHex(l.colorIndex ?? 7);
        layerColors.set(l.name ?? key, hex);
        layers.push({
            name: l.name ?? key,
            visible: l.visible !== false,
            frozen: !!l.frozen,
            locked: !!(l as any).locked,
            color: hex,
        });
    }

    const blocks = new Map<string, RawDxfEntity[]>();
    for (const [name, blk] of Object.entries((dxf as any).blocks ?? {})) {
        const b = blk as { name?: string; entities?: RawDxfEntity[] };
        blocks.set(b.name ?? name, b.entities ?? []);
    }

    return { insunits, entities: (dxf.entities ?? []) as RawDxfEntity[], blocks, layerColors, layers };
}

async function parseDwg(fileName: string, bytes: ArrayBuffer): Promise<CadParseResult> {
    const dwg = await getLibreDwg();
    let ptr: number | undefined;
    try {
        ptr = dwg.dwg_read_data(bytes, 0 /* DWG */);
    } catch (err) {
        throw new Error(`DWG read failed for ${fileName}: ${(err as Error).message}`);
    }
    if (ptr == null) {
        throw new Error(`Unsupported or corrupt DWG: ${fileName} (LibreDWG could not decode it).`);
    }
    let db: any;
    try {
        db = dwg.convert(ptr);
    } finally {
        try { dwg.dwg_free(ptr); } catch { /* ignore */ }
    }
    const insunits = readDwgInsunits(db?.header);
    const entities = dwgToRawEntities(db);
    const blocks = dwgBlockMap(db);
    const layerColors = dwgLayerColors(db);
    const layers: CadLayerInfo[] = (db?.tables?.LAYER?.entries ?? []).map((l: any) => ({
        name: l.name,
        visible: !l.off,
        frozen: !!l.frozen,
        locked: !!l.locked,
        color: aciToHex(typeof l.colorIndex === 'number' ? l.colorIndex : 7),
    }));
    return finish({ insunits, entities, blocks, layerColors, layers }, fileName, 'dwg');
}

function finish(
    parsed: { insunits?: number; entities: RawDxfEntity[]; blocks: Map<string, RawDxfEntity[]>; layerColors: Map<string, string>; layers: CadLayerInfo[] },
    fileName?: string,
    format: 'dxf' | 'dwg' = 'dxf'
): CadParseResult {
    const scale = insunitsToMeters(parsed.insunits);
    const lines: CadLine[] = [];
    const texts: CadText[] = [];
    const budget = { left: MAX_ENTITIES_PER_PARSE };
    parseDxfEntities(parsed.entities, lines, texts, budget, parsed.layerColors, parsed.blocks, scale);

    // Block definitions for the native-import path (Faz 9.5): each BLOCKS
    // entry parsed into its own geometry bag, sharing the parse budget.
    const blocksOut: CadBlockInfo[] = [];
    if (parsed.blocks.size > 0 && budget.left > 0) {
        const blockBudget = { left: Math.min(budget.left, 100_000) };
        for (const [name, ents] of parsed.blocks) {
            if (blocksOut.length >= 200 || blockBudget.left <= 0) break;
            if (!Array.isArray(ents) || ents.length === 0 || !name) continue;
            const bLines: CadLine[] = [];
            const bTexts: CadText[] = [];
            parseDxfEntities(ents, bLines, bTexts, blockBudget, parsed.layerColors, parsed.blocks, scale);
            if (bLines.length > 0 || bTexts.length > 0) {
                blocksOut.push({ name, lines: bLines, texts: bTexts });
            }
        }
    }

    // Underlay references (Faz 9.8): keep the source path + placement so
    // the Xref panel can list the reference and flag it as missing.
    const underlays: CadUnderlayInfo[] = [];
    for (const e of parsed.entities) {
        const type = String((e as any).type ?? '');
        if (type !== 'IMAGE' && type !== 'PDFUNDERLAY' && type !== 'UNDERLAY') continue;
        const path = (e as any).imageDefPath ?? (e as any).imageDefRefName ?? (e as any).path
            ?? (e as any).filePath ?? (e as any).imageDefHandle ?? '';
        const pos = (e as any).insertionPoint ?? (e as any).position;
        const sz = (e as any).sizeInWorldUnits === 1 ? (e as any).size : null;
        const w = ((sz && sz.x) ?? (e as any).width ?? 1) * scale;
        const h = ((sz && sz.y) ?? (e as any).height ?? 1) * scale;
        underlays.push({
            name: String((e as any).name ?? ''),
            path: String(path),
            layer: e.layer ?? '0',
            insertion: pos
                ? [pos.x * scale, pos.y * scale, ((pos.z ?? 0)) * scale]
                : [0, 0, 0],
            width: w,
            height: h,
        });
    }

    // Compute bounds (include text anchors so labels zoom correctly)
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    const eat = (x: number, y: number, z: number) => {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
        if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
    };
    for (const ln of lines) { ln.points.forEach(p => eat(p[0], p[1], p[2])); }
    for (const tx of texts) eat(tx.x, tx.y, tx.z);
    if (!Number.isFinite(minX)) { minX = minY = minZ = 0; maxX = maxY = maxZ = 0; }

    return {
        fileName: fileName ?? '',
        format,
        bounds: [minX, minY, minZ, maxX, maxY, maxZ],
        unitScale: scale,
        insunits: parsed.insunits ?? 0,
        lines, texts,
        layers: parsed.layers,
        blocks: blocksOut.length > 0 ? blocksOut : undefined,
        underlays: underlays.length > 0 ? underlays : undefined,
    };
}

/**
 * True when the extension can be parsed locally (DXF pure-JS; DWG family —
 * .dwg/.dwt/.dws share the LibreDWG wasm reader). Used to give a precise
 * error for SVG/other files before attempting a parse.
 */
export function isParsableCad(fileName: string): boolean {
    const l = fileName.toLowerCase();
    return l.endsWith('.dwg') || l.endsWith('.dxf') || l.endsWith('.dwt') || l.endsWith('.dws');
}
